import { createContext, useContext, useRef, useState, useEffect, useCallback, type ReactNode } from 'react';
import { AppLock, type AppLockInstance } from '@ascend/applock';

const DEFERRED_KEY = 'slot-matrix-applock-decision';

export type LockDecision = 'pending' | 'skipped' | 'enabled';

const APPLOCK_META_KEYS = ['applock_hash', 'applock_salt', 'applock_config', 'applock_version'];

function clearAppLockMetaKeys() {
  for (const key of APPLOCK_META_KEYS) {
    localStorage.removeItem(key);
  }
}

function getStoredDecision(): LockDecision {
  const stored = localStorage.getItem(DEFERRED_KEY);
  if (stored === 'skipped') return 'skipped';
  if (stored === 'enabled') return 'enabled';
  return 'pending';
}

function createLock(): AppLockInstance {
  return AppLock.init({
    mode: 'encrypted',
    storageBackend: 'indexedDB',
    idbDatabaseName: 'slot-matrix',
    idbStoreName: 'sessions',
    appRootSelector: '#root',
    lockOnHide: false,
    idleTimeout: 900,
    ui: false,
  });
}

interface LockState {
  lock: AppLockInstance | null;
  isLocked: boolean;
  isUnlocked: boolean;
  isConfigured: boolean;
  decision: LockDecision;
}

interface AppLockContextValue extends LockState {
  isEnabled: boolean;
  skipEncryption: () => void;
  enableEncryption: () => void;
}

const AppLockContext = createContext<AppLockContextValue | null>(null);

function getInitialState(): LockState {
  const decision = getStoredDecision();
  return {
    lock: null,
    isLocked: false,
    isUnlocked: decision === 'skipped',
    isConfigured: false,
    decision,
  };
}

export function AppLockProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LockState>(getInitialState);
  const instanceRef = useRef<AppLockInstance | null>(null);

  const skipEncryption = useCallback(() => {
    localStorage.setItem(DEFERRED_KEY, 'skipped');
    clearAppLockMetaKeys();
    if (instanceRef.current) {
      instanceRef.current.destroy();
      instanceRef.current = null;
    }
    setState({
      lock: null,
      isLocked: false,
      isUnlocked: true,
      isConfigured: false,
      decision: 'skipped',
    });
  }, []);

  const enableEncryption = useCallback(() => {
    localStorage.setItem(DEFERRED_KEY, 'enabled');
    clearAppLockMetaKeys();
    setState(prev => ({
      ...prev,
      decision: 'enabled',
      isUnlocked: false,
      isConfigured: false,
    }));
  }, []);

  useEffect(() => {
    if (state.decision !== 'enabled') return;

    const instance = createLock();
    instanceRef.current = instance;

    const onLock = () => {
      setState(prev => ({ ...prev, isLocked: true, isUnlocked: false }));
    };
    const onUnlock = () => {
      setState(prev => ({ ...prev, isLocked: false, isUnlocked: true, isConfigured: true }));
    };
    const onPasswordClear = () => {
      setState(prev => ({ ...prev, isConfigured: false }));
    };
    const onSetup = () => {
      setState(prev => ({ ...prev, isConfigured: true }));
    };

    instance.on('lock', onLock);
    instance.on('unlock', onUnlock);
    instance.on('password:clear', onPasswordClear);
    instance.on('setup', onSetup);

    setState(prev => ({
      ...prev,
      lock: instance,
      isLocked: instance.isLocked,
      isUnlocked: instance.state === 'unlocked' || instance.state === 'uninitialized',
      isConfigured: instance.isConfigured,
    }));

    return () => {
      instance.destroy();
      instanceRef.current = null;
    };
  }, [state.decision]);

  if (state.decision === 'enabled' && !state.lock) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ fontSize: 'var(--font-size-md)', color: 'var(--text-secondary)' }}>Initializing…</div>
      </div>
    );
  }

  return (
    <AppLockContext.Provider
      value={{
        ...state,
        isEnabled: state.decision === 'enabled' && state.lock !== null,
        skipEncryption,
        enableEncryption,
      }}
    >
      {children}
    </AppLockContext.Provider>
  );
}

export function useAppLock(): AppLockContextValue {
  const ctx = useContext(AppLockContext);
  if (!ctx) throw new Error('useAppLock must be used within AppLockProvider');
  return ctx;
}
